import * as React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { createEventSchema, type EventFormValues } from '../schemas';
import { useEventMutations } from '../hooks';
import type { CalendarEvent } from '../types';
import { EventDialogForm } from './EventDialogForm';

interface EventDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  event?: CalendarEvent | null | undefined;
  defaultDate?: Date | undefined;
  onSuccess?: (() => void) | undefined;
}

interface MatterDetails {
  id: number;
  name?: string;
  judgeName?: string;
  courtName?: string;
  courtEmail?: string;
  courtPhone?: string;
}

export function EventDialog({
  open,
  onOpenChange,
  event,
  defaultDate,
  onSuccess,
}: EventDialogProps) {
  const { t } = useTranslation();
  const { createMutation, updateMutation } = useEventMutations();
  const [selectedMatter, setSelectedMatter] = React.useState<MatterDetails | undefined>(undefined);
  const [editEventId, setEditEventId] = React.useState<number | null>(null);

  React.useEffect(() => {
    if (open && event?.id) {
      setEditEventId(event.id);
    } else if (!open) {
      setEditEventId(null);
    }
  }, [open, event?.id]);

  const isEditMode = editEventId !== null;
  const eventSchema = React.useMemo(() => createEventSchema(t), [t]);

  const form = useForm<EventFormValues>({
    resolver: zodResolver(eventSchema),
    defaultValues: {
      description: '',
      location: '',
      startsAt: defaultDate ?? new Date(),
      endsAt: defaultDate ? new Date(defaultDate.getTime() + 60 * 60 * 1000) : new Date(),
      attendees: [],
      target: 'FIRM_EVENT',
      matterId: undefined,
      clientId: undefined,
    },
  });

  const watchTarget = form.watch('target');
  const formInitializedRef = React.useRef(false);

  React.useEffect(() => {
    if (!open) {
      formInitializedRef.current = false;
    }
  }, [open]);

  React.useEffect(() => {
    if (!open || formInitializedRef.current) return;

    if (event) {
      form.reset({
        description: event.description ?? '',
        location: event.location ?? '',
        startsAt: new Date(event.startsAt),
        endsAt: new Date(event.endsAt),
        attendees: event.attendees?.map((a) => a.id) ?? [],
        target: event.target ?? 'FIRM_EVENT',
        matterId: event.matterId,
        clientId: event.clientId,
      });
      if (event.matter) {
        setSelectedMatter(event.matter as MatterDetails);
      }
      formInitializedRef.current = true;
    } else if (defaultDate) {
      form.reset({
        description: '',
        location: '',
        startsAt: defaultDate,
        endsAt: new Date(defaultDate.getTime() + 60 * 60 * 1000),
        attendees: [],
        target: 'FIRM_EVENT',
        matterId: undefined,
        clientId: undefined,
      });
      setSelectedMatter(undefined);
      formInitializedRef.current = true;
    }
  }, [event, open, defaultDate, form]);

  const prevTargetRef = React.useRef<string | undefined>(undefined);
  React.useEffect(() => {
    if (!formInitializedRef.current) {
      prevTargetRef.current = watchTarget;
      return;
    }
    if (prevTargetRef.current !== undefined && watchTarget !== 'CASE_MEETING') {
      form.setValue('matterId', undefined);
      form.setValue('clientId', undefined);
      setSelectedMatter(undefined);
    }
    prevTargetRef.current = watchTarget;
  }, [watchTarget, form]);

  const onSubmit = async (values: EventFormValues) => {
    const payload = {
      description: values.description,
      startsAt: values.startsAt.toISOString(),
      endsAt: values.endsAt.toISOString(),
      location: values.location || undefined,
      target: values.target,
      attendees: values.attendees,
      matterId: values.target === 'CASE_MEETING' ? values.matterId : undefined,
    };

    if (isEditMode && editEventId) {
      await updateMutation.mutateAsync({ id: editEventId, data: payload });
    } else {
      await createMutation.mutateAsync(payload);
    }

    form.reset();
    setSelectedMatter(undefined);
    setEditEventId(null);
    onOpenChange(false);
    onSuccess?.();
  };

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditMode ? t('update') : t('new')} {t('events')}
          </DialogTitle>
        </DialogHeader>

        <EventDialogForm
          form={form}
          selectedMatter={selectedMatter}
          onMatterSelect={setSelectedMatter}
          onSubmit={onSubmit}
          onCancel={() => onOpenChange(false)}
          isSubmitting={isSubmitting}
        />
      </DialogContent>
    </Dialog>
  );
}
